import React, { ReactElement, useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, FormField, Modal, Select, SpaceBetween } from '@amzn/awsui-components-react-v3';

import useLocalStorage from '../../hooks/useLocalStorage';
import { Site } from '../../store/types';
import useGetSites from './useGetSites';

interface SiteInputProps {
  onSiteChange?: (siteOption: Site) => void;
  siteName?: string;
  isPreferredSite?: boolean;
  isDisabled?: boolean;
}

const SiteInput = ({ onSiteChange, siteName, isPreferredSite, isDisabled }: SiteInputProps): ReactElement => {
  const [locationStore] = useLocalStorage('locationStore');
  const locationStorage = useMemo(() => (locationStore ? locationStore : []), [locationStore]);
  const [locations, setLocations] = useState([]);
  const [modalVisible, setModalVisible] = useState(isPreferredSite && !siteName);
  const [closedWithoutSiteSelection, setClosedWithoutSiteSelection] = useState(false);

  // if SiteOption does not match the siteName
  const getUpdatedSelectedSiteOption = (siteName: string | undefined): any => {
    if (siteName && siteName[3] === '0') {
      const updatedSiteName = siteName.substring(0, 3) + siteName.substring(4);
      return [...locations, ...locationStorage].find((site: Site) => site?.value === updatedSiteName);
    }

    return [...locations, ...locationStorage].find((site: Site) => site?.value === siteName);
  };

  let selectedSiteOption = getUpdatedSelectedSiteOption(siteName);

  const { getSites, loading } = useGetSites({
    onCompleted(locations) {
      setLocations(locations);
      const site = locations.find((site: Site) => site?.value === siteName);
      if (!site && isPreferredSite) {
        setModalVisible(true);
      }
    },
  });

  /* istanbul ignore next */
  const handleModalClose = () => {
    if (selectedSiteOption) {
      setModalVisible(false);
    } else {
      setClosedWithoutSiteSelection(true);
    }
  };

  /* istanbul ignore next */
  const handleSiteChange = (obj: any) => {
    if (onSiteChange) {
      onSiteChange(obj);
    }
    setModalVisible(false);
  };

  const handleOnEdit = () => {
    setModalVisible(true);
  };

  const getSiteDropdowns = () => {
    return (
      <Select
        selectedOption={selectedSiteOption}
        onChange={({ detail }) => handleSiteChange(detail.selectedOption)}
        options={locations}
        data-testid={loading ? 'site-dropdown-loading' : 'site-dropdown-ok'}
        placeholder="Choose an option"
        selectedAriaLabel="Selected"
        filteringType="auto"
        empty="No options"
        expandToViewport
        virtualScroll
        disabled={isDisabled}
      />
    );
  };

  useEffect(() => {
    getSites({ variables: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isPreferredSite) {
    return getSiteDropdowns();
  }

  return (
    <div aria-label={`Select from total locations ${selectedSiteOption}`}>
      <Modal
        visible={!!modalVisible}
        closeAriaLabel="Close modal"
        onDismiss={() => handleModalClose()}
        data-test-id="modal-dropdown"
        header={selectedSiteOption ? 'Update Preferred Site' : 'Select Preferred Site'}
      >
        <SpaceBetween size="l" direction="vertical">
          {selectedSiteOption && (
            <Alert statusIconAriaLabel="Info">
              You are updating the preferred site. This site will be defaulted for further receives.
            </Alert>
          )}
          <FormField label="Site*">{getSiteDropdowns()}</FormField>
          {closedWithoutSiteSelection && (
            <Alert statusIconAriaLabel="Error" type="error">
              Please select a preferred site.
            </Alert>
          )}
        </SpaceBetween>
      </Modal>
      {selectedSiteOption && isPreferredSite && (
        <Box padding="xs" variant="awsui-key-label">
          {selectedSiteOption?.label}
          <Button iconName="edit" ariaLabel="edit-site" onClick={handleOnEdit} variant="inline-icon" />
        </Box>
      )}
    </div>
  );
};

export default SiteInput;
